import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { BiomeKind } from '../generated/BiomeKind';
import {
  getMapTiles,
  type MapTileDataJson,
  type ClaimOverlayJson,
  type ClaimTilesOverlayJson,
  type ChunkCoordJson,
  type RiverSegmentJson,
  type LakeOverlayJson,
} from '../platform/api';
import { useEmbark, chunkKey, BLOCK_SIZE, type PaintTool } from '../platform/store';
import { openMapCache, idbKey, type MapCache } from './map-cache';
import { useToasts } from '../toasts';
import { debugLog } from '../log';
import { ClaimPanel } from './ClaimPanel';
import { ClaimTooltip, type ClaimTooltipData } from './ClaimTooltip';
import { dominantBiome } from './biomeLabel';
import {
  brushPaint,
  brushPreview,
  candidateSet,
  checkContiguityLocal,
  computeCostPreview,
  fillByBiome,
  fillPreview,
  isBorderChunk,
  snapToRiver,
  type TileLookup,
} from './PaintTools';
import {
  createMapRenderer,
  computeRiverBounds,
  computeLakeBounds,
  type MapRenderer,
  type RenderState,
  type BoundedRiver,
  type BoundedLake,
  type ViewState,
  type CachedRegion,
} from './mapRenderer';
import styles from './embark.module.css';

const CELL_SIZE = 8;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 8;
const INITIAL_CHUNKS = 50;
const FETCH_MARGIN = 10;
const DEBOUNCE_MS = 200;
const CHUNK_TILES = 128;
const RIVER_ZOOM_THRESHOLD = 0.5;
const GRID = 64;
const OVERLAY_THROTTLE_MS = 2000;
const PREFETCH_CELL_LIMIT = 80;
const MAX_INFLIGHT = 20;

function getVisibleBounds(
  el: HTMLElement,
  view: ViewState,
): { x1: number; y1: number; x2: number; y2: number } {
  const cellPx = CELL_SIZE * view.zoom;
  const x1 = Math.floor(-view.offsetX / cellPx) - FETCH_MARGIN;
  const y1 = Math.floor(-view.offsetY / cellPx) - FETCH_MARGIN;
  const x2 = Math.ceil((el.clientWidth - view.offsetX) / cellPx) + FETCH_MARGIN;
  const y2 = Math.ceil((el.clientHeight - view.offsetY) / cellPx) + FETCH_MARGIN;
  return { x1, y1, x2, y2 };
}

function clampBounds(b: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): { x1: number; y1: number; x2: number; y2: number } {
  const maxSpan = 99;
  let { x1, y1, x2, y2 } = b;
  if (x2 - x1 > maxSpan) x2 = x1 + maxSpan;
  if (y2 - y1 > maxSpan) y2 = y1 + maxSpan;
  return { x1, y1, x2, y2 };
}

function lookupTile(
  regions: Map<string, CachedRegion>,
  cx: number,
  cy: number,
): MapTileDataJson | null {
  for (const region of regions.values()) {
    const ry = cy - region.y1;
    const rx = cx - region.x1;
    if (ry >= 0 && ry < region.tiles.length) {
      const row = region.tiles[ry]!;
      if (rx >= 0 && rx < row.length) return row[rx]!;
    }
  }
  return null;
}

function fmtElevation(e: number): string {
  return `${Math.round(e * 1000)}m`;
}
function fmtTemperature(t: number): string {
  return `${Math.round(t)}°C`;
}
function fmtMoisture(m: number): string {
  return `${Math.round(m * 100)}%`;
}

const TOOL_LABELS: Record<PaintTool, string> = {
  brush: 'Brush (B)',
  fill: 'Fill (F)',
  snap: 'Snap (S)',
  eraser: 'Eraser (E)',
};

const BRUSH_STEPS = [1, 2, 3, 4, 5, 8, 12, 16, 24, 32, 48, 64, 96, 128];

export function WorldMap(): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<MapRenderer | null>(null);
  const rafRef = useRef<number | null>(null);
  const needsFrameRef = useRef(false);
  const viewRef = useRef<ViewState>({
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startOffX: number;
    startOffY: number;
  } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoverChunk, setHoverChunk] = useState<ChunkCoordJson | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedOwnClaim, setSelectedOwnClaim] = useState<number | null>(null);
  const regionsRef = useRef<Map<string, CachedRegion>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());
  const mapCacheRef = useRef<MapCache | null>(null);
  const claimsRef = useRef<ClaimOverlayJson[]>([]);
  const claimTilesRef = useRef<ClaimTilesOverlayJson[]>([]);
  const claimBitmapRef = useRef<{
    own: { canvas: OffscreenCanvas; originX: number; originY: number } | null;
    other: { canvas: OffscreenCanvas; originX: number; originY: number } | null;
    version: number;
  }>({ own: null, other: null, version: -1 });
  const riversRef = useRef<RiverSegmentJson[]>([]);
  const lakesRef = useRef<LakeOverlayJson[]>([]);
  const boundedRiversRef = useRef<BoundedRiver[]>([]);
  const boundedLakesRef = useRef<BoundedLake[]>([]);
  const overlayBoundsRef = useRef<{ x1: number; y1: number; x2: number; y2: number; time: number } | null>(null);
  const paintingRef = useRef(false);
  const strokeChunksRef = useRef<ChunkCoordJson[]>([]);
  const hoverClaimRef = useRef<{
    claimId: number;
    screenX: number;
    screenY: number;
  } | null>(null);
  const [hoverClaim, setHoverClaim] = useState<{
    claimId: number;
    screenX: number;
    screenY: number;
  } | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const biomeCacheRef = useRef<Map<number, BiomeKind | null>>(new Map());

  const currentWorld = useEmbark((s) =>
    s.megaWorlds.find((w) => w.id === s.selectedMegaWorld),
  );
  const worldW = currentWorld?.width ?? 0;
  const worldH = currentWorld?.height ?? 0;
  const generationId = currentWorld?.generation_id ?? '';

  useEffect(() => {
    for (const region of regionsRef.current.values()) {
      if (region.canvas) {
        region.canvas.width = 0;
        region.canvas.height = 0;
        region.canvas = null;
      }
    }
    regionsRef.current.clear();
    inFlightRef.current.clear();
    overlayBoundsRef.current = null;
    claimBitmapRef.current = { own: null, other: null, version: -1 };
  }, [currentWorld?.id]);

  useEffect(() => {
    let stale = false;
    openMapCache().then((mc) => {
      if (stale) { mc.close(); return; }
      mapCacheRef.current = mc;
    });
    return () => {
      stale = true;
      mapCacheRef.current?.close();
      mapCacheRef.current = null;
      for (const region of regionsRef.current.values()) {
        if (region.canvas) { region.canvas.width = 0; region.canvas.height = 0; }
      }
      regionsRef.current.clear();
    };
  }, []);

  const clampView = useCallback((v: ViewState, el: HTMLElement) => {
    if (worldW === 0 || worldH === 0) return;
    const cellPx = CELL_SIZE * v.zoom;
    const mapPxW = worldW * cellPx;
    const mapPxH = worldH * cellPx;
    v.offsetX = Math.max(el.clientWidth - mapPxW, Math.min(0, v.offsetX));
    v.offsetY = Math.max(el.clientHeight - mapPxH, Math.min(0, v.offsetY));
  }, [worldW, worldH]);

  const selectedChunk = useEmbark((s) => s.selectedChunk);
  const visibleClaims = useEmbark((s) => s.visibleClaims);
  const myClaims = useEmbark((s) => s.myClaims);
  const fetchRiversAndLakes = useEmbark((s) => s.fetchRiversAndLakes);
  const fetchClaims = useEmbark((s) => s.fetchClaims);
  const claimTiles = useEmbark((s) => s.claimTiles);
  const fetchClaimTiles = useEmbark((s) => s.fetchClaimTiles);
  const selectChunk = useEmbark((s) => s.selectChunk);
  const rivers = useEmbark((s) => s.rivers);
  const lakes = useEmbark((s) => s.lakes);

  const expandingClaimId = useEmbark((s) => s.expandingClaimId);
  const expandingClaim = useEmbark((s) => s.expandingClaim);
  const expandingChunks = useEmbark((s) => s.expandingChunks);
  const pendingAdditions = useEmbark((s) => s.pendingAdditions);
  const pendingRemovals = useEmbark((s) => s.pendingRemovals);
  const activeTool = useEmbark((s) => s.activeTool);
  const brushSize = useEmbark((s) => s.brushSize);
  const undoStack = useEmbark((s) => s.undoStack);
  const validationErrors = useEmbark((s) => s.validationErrors);
  const islandChunks = useEmbark((s) => s.islandChunks);
  const commitInProgress = useEmbark((s) => s.commitInProgress);
  const enterExpansion = useEmbark((s) => s.enterExpansion);
  const exitExpansion = useEmbark((s) => s.exitExpansion);
  const setActiveTool = useEmbark((s) => s.setActiveTool);
  const setBrushSize = useEmbark((s) => s.setBrushSize);
  const paintChunks = useEmbark((s) => s.paintChunks);
  const eraseChunks = useEmbark((s) => s.eraseChunks);
  const undo = useEmbark((s) => s.undo);
  const commitExpansion = useEmbark((s) => s.commitExpansion);

  const inExpansion = expandingClaimId != null;

  claimsRef.current = visibleClaims;
  claimTilesRef.current = claimTiles;
  biomeCacheRef.current.clear();

  useMemo(() => {
    const prev = claimBitmapRef.current;
    if (prev.own) prev.own.canvas.width = 0;
    if (prev.other) prev.other.canvas.width = 0;

    const ownTiles: ChunkCoordJson[] = [];
    const otherTiles: ChunkCoordJson[] = [];
    for (const ct of claimTiles) {
      if (ct.claim_id === expandingClaimId) continue;
      if (ct.is_own) {
        for (const t of ct.tiles) ownTiles.push(t);
      } else {
        for (const t of ct.tiles) otherTiles.push(t);
      }
    }
    const buildBitmap = (tiles: ChunkCoordJson[], color: string) => {
      if (tiles.length === 0) return null;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const t of tiles) {
        if (t.x < minX) minX = t.x;
        if (t.x > maxX) maxX = t.x;
        if (t.y < minY) minY = t.y;
        if (t.y > maxY) maxY = t.y;
      }
      const w = maxX - minX + 1;
      const h = maxY - minY + 1;
      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = color;
      for (const t of tiles) {
        ctx.fillRect(t.x - minX, t.y - minY, 1, 1);
      }
      return { canvas, originX: minX, originY: minY };
    };
    claimBitmapRef.current = {
      own: buildBitmap(ownTiles, 'rgba(88, 101, 242, 0.35)'),
      other: buildBitmap(otherTiles, 'rgba(237, 66, 69, 0.25)'),
      version: claimBitmapRef.current.version + 1,
    };
  }, [claimTiles, expandingClaimId]);

  useEffect(() => {
    riversRef.current = rivers;
    boundedRiversRef.current = rivers.map(computeRiverBounds);
  }, [rivers]);

  useEffect(() => {
    lakesRef.current = lakes;
    boundedLakesRef.current = lakes.map(computeLakeBounds);
  }, [lakes]);

  const claimedSet = useMemo((): Set<string> => {
    const s = new Set<string>();
    for (const ct of claimTiles) {
      for (const t of ct.tiles) s.add(chunkKey(t));
    }
    return s;
  }, [claimTiles]);

  const allClaimedSet = useCallback((): Set<string> => claimedSet, [claimedSet]);

  const tileLookup: TileLookup = {
    get: (cx: number, cy: number) => lookupTile(regionsRef.current, cx, cy),
  };

  const isBlockAreaClaimed = useCallback(
    (bx: number, by: number): boolean => {
      const claimed = allClaimedSet();
      for (let dy = 0; dy < BLOCK_SIZE; dy++) {
        for (let dx = 0; dx < BLOCK_SIZE; dx++) {
          if (claimed.has(chunkKey({ x: bx + dx, y: by + dy }))) return true;
        }
      }
      return false;
    },
    [allClaimedSet],
  );

  const screenToChunk = useCallback(
    (clientX: number, clientY: number): ChunkCoordJson | null => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const v = viewRef.current;
      const cellPx = CELL_SIZE * v.zoom;
      const cx = Math.floor((px - v.offsetX) / cellPx);
      const cy = Math.floor((py - v.offsetY) / cellPx);
      return { x: cx, y: cy };
    },
    [],
  );

  const findClaimAtChunk = useCallback(
    (chunk: ChunkCoordJson): { claimId: number; isOwn: boolean } | null => {
      const ck = chunkKey(chunk);
      for (const ct of claimTilesRef.current) {
        if (ct.tiles.some((t) => chunkKey(t) === ck)) {
          return { claimId: ct.claim_id, isOwn: ct.is_own };
        }
      }
      return null;
    },
    [],
  );

  const computeClaimBiome = useCallback(
    (claimId: number): BiomeKind | null => {
      const cached = biomeCacheRef.current.get(claimId);
      if (cached !== undefined) return cached;

      const ct = claimTilesRef.current.find((c) => c.claim_id === claimId);
      if (!ct) return null;

      const regions = regionsRef.current;
      const biomes: BiomeKind[] = [];
      for (const tile of ct.tiles) {
        const td = lookupTile(regions, tile.x, tile.y);
        if (td) biomes.push(td.biome);
      }

      const threshold = ct.tiles.length * 0.5;
      const result = biomes.length >= threshold ? dominantBiome(biomes) : null;
      biomeCacheRef.current.set(claimId, result);
      return result;
    },
    [],
  );

  // Client-side contiguity check results
  const disconnectedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!inExpansion || !expandingClaim) {
      disconnectedRef.current = new Set();
      return;
    }
    const cs = candidateSet(expandingChunks, pendingAdditions, pendingRemovals);
    const disc = checkContiguityLocal(expandingClaim.origin, cs);
    disconnectedRef.current = new Set(disc.map(chunkKey));
  }, [inExpansion, expandingClaim, expandingChunks, pendingAdditions, pendingRemovals]);

  // Removed bulk IDB preload — it loaded the entire 200MB cache into RAM at once.
  // fetchCell now checks IDB lazily per-cell before hitting the network.

  const buildRenderState = useCallback((): RenderState => {
    const v = viewRef.current;
    return {
      view: { offsetX: v.offsetX, offsetY: v.offsetY, zoom: v.zoom },
      regions: regionsRef.current,
      rivers: boundedRiversRef.current,
      lakes: boundedLakesRef.current,
      claimBitmaps: claimBitmapRef.current,
      hoverChunk,
      selectedChunk,
      showGrid,
      showPanel,
      inExpansion,
      expandingChunks,
      pendingAdditions,
      pendingRemovals,
      validationErrors,
      islandChunks,
      disconnected: disconnectedRef.current,
      activeTool,
      brushSize,
      worldW,
      worldH,
      isBlockAreaClaimed,
      allClaimedSet: () => allClaimedSet(),
      tileLookup: (cx: number, cy: number) => lookupTile(regionsRef.current, cx, cy),
      brushPreviewChunks: (inExpansion && hoverChunk && activeTool === 'brush' && brushSize > 1)
        ? brushPreview(hoverChunk.x, hoverChunk.y, brushSize)
        : [],
      fillPreviewChunks: (() => {
        if (!inExpansion || !hoverChunk || activeTool !== 'fill') return [];
        const ck = `${hoverChunk.x},${hoverChunk.y}`;
        const owned = expandingChunks.has(ck) || pendingAdditions.has(ck);
        const claimed = allClaimedSet();
        if (owned || claimed.has(ck)) return [];
        const tile = lookupTile(regionsRef.current, hoverChunk.x, hoverChunk.y);
        if (!tile) return [];
        return fillPreview(
          hoverChunk.x, hoverChunk.y, tile.biome,
          expandingChunks, pendingAdditions, claimed, tileLookup,
        );
      })(),
    };
  }, [
    hoverChunk, selectedChunk, showGrid, showPanel,
    inExpansion, expandingChunks, pendingAdditions, pendingRemovals,
    validationErrors, islandChunks, activeTool, brushSize,
    worldW, worldH, isBlockAreaClaimed, allClaimedSet,
  ]);

  const scheduleFrame = useCallback(() => {
    needsFrameRef.current = true;
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (!needsFrameRef.current) return;
      needsFrameRef.current = false;
      const renderer = rendererRef.current;
      if (!renderer) return;
      renderer.frame(buildRenderState());
    });
  }, [buildRenderState]);

  const fetchCell = useCallback(async (
    cellX: number, cellY: number, mw: string, genId: string,
  ) => {
    const regionKey = `${cellX},${cellY}`;
    if (regionsRef.current.has(regionKey)) return;
    if (inFlightRef.current.has(regionKey)) return;
    inFlightRef.current.add(regionKey);

    const bounds = {
      x1: cellX * GRID,
      y1: cellY * GRID,
      x2: cellX * GRID + GRID - 1,
      y2: cellY * GRID + GRID - 1,
    };
    try {
      const cacheKey = idbKey(mw, genId, cellX, cellY);
      const cached = await mapCacheRef.current?.get(cacheKey);
      if (cached) {
        regionsRef.current.set(regionKey, {
          x1: bounds.x1, y1: bounds.y1, tiles: cached,
          canvas: null, canvasHillshade: false,
        });
        debugLog('world', 'debug', `cell(${cellX},${cellY}) from idb`);
      } else {
        const t0 = performance.now();
        const tiles = await getMapTiles(mw, bounds);
        const dt = performance.now() - t0;
        regionsRef.current.set(regionKey, {
          x1: bounds.x1, y1: bounds.y1, tiles,
          canvas: null, canvasHillshade: false,
        });
        mapCacheRef.current?.put(cacheKey, tiles).catch(() => {});
        debugLog('world', 'debug', `cell(${cellX},${cellY}) fetched ${dt.toFixed(0)}ms`);
      }
      rendererRef.current?.invalidate('terrain');
      scheduleFrame();
    } catch (e) {
      debugLog('world', 'error', `cell(${cellX},${cellY}) failed: ${e instanceof Error ? e.message : String(e)}`);
      useToasts.getState().push(
        'error',
        `Failed to load map cell: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      inFlightRef.current.delete(regionKey);
    }
  }, [scheduleFrame]);

  const loadRegion = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;
    const mw = currentWorld?.id;
    if (!mw) return;

    if (inFlightRef.current.size > MAX_INFLIGHT) {
      debugLog('world', 'debug', `loadRegion skipped: ${inFlightRef.current.size} in-flight`);
      return;
    }

    const genId = generationId;
    const zoom = viewRef.current.zoom;
    const raw = getVisibleBounds(container, viewRef.current);
    const visible = {
      x1: raw.x1,
      y1: worldH > 0 ? Math.max(0, raw.y1) : raw.y1,
      x2: raw.x2,
      y2: worldH > 0 ? Math.min(worldH - 1, raw.y2) : raw.y2,
    };
    if (visible.y1 > visible.y2) return;

    const cx0 = Math.floor(visible.x1 / GRID);
    const cy0 = Math.floor(visible.y1 / GRID);
    const cx1 = Math.floor(visible.x2 / GRID);
    const cy1 = Math.floor(visible.y2 / GRID);

    const cells: { cellX: number; cellY: number }[] = [];
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        cells.push({ cellX: cx, cellY: cy });
      }
    }

    const centerCX = (cx0 + cx1) / 2;
    const centerCY = (cy0 + cy1) / 2;

    const maxCached = Math.max(200, cells.length + 50);
    if (regionsRef.current.size > maxCached) {
      const visibleKeys = new Set(cells.map((c) => `${c.cellX},${c.cellY}`));
      const entries = [...regionsRef.current.keys()];
      entries.sort((a, b) => {
        const [ax, ay] = a.split(',').map(Number) as [number, number];
        const [bx, by] = b.split(',').map(Number) as [number, number];
        const da = (ax - centerCX) ** 2 + (ay - centerCY) ** 2;
        const db = (bx - centerCX) ** 2 + (by - centerCY) ** 2;
        return db - da;
      });
      let evicted = 0;
      for (const key of entries) {
        if (regionsRef.current.size <= maxCached) break;
        if (visibleKeys.has(key)) continue;
        const region = regionsRef.current.get(key);
        if (region?.canvas) {
          region.canvas.width = 0;
          region.canvas.height = 0;
          region.canvas = null;
        }
        regionsRef.current.delete(key);
        evicted++;
      }
      if (evicted > 0) {
        debugLog('world', 'info', `evicted ${evicted} cached regions (${regionsRef.current.size} remaining, cap=${maxCached})`);
      }
    }

    const toFetch = cells.filter(
      (c) => {
        const k = `${c.cellX},${c.cellY}`;
        return !regionsRef.current.has(k) && !inFlightRef.current.has(k);
      },
    );
    toFetch.sort((a, b) => {
      const da = (a.cellX - centerCX) ** 2 + (a.cellY - centerCY) ** 2;
      const db = (b.cellX - centerCX) ** 2 + (b.cellY - centerCY) ** 2;
      return da - db;
    });

    debugLog('world', 'info', `loadRegion cells=${cells.length} toFetch=${toFetch.length} zoom=${zoom.toFixed(2)} cached=${regionsRef.current.size} cap=${maxCached}`);

    const MAX_CONCURRENT = 6;
    let inFlight = 0;
    let idx = 0;
    const dispatch = () => {
      while (inFlight < MAX_CONCURRENT && idx < toFetch.length) {
        const c = toFetch[idx++]!;
        inFlight++;
        fetchCell(c.cellX, c.cellY, mw, genId).finally(() => {
          inFlight--;
          dispatch();
        });
      }
    };
    dispatch();

    const now = Date.now();
    const prevOv = overlayBoundsRef.current;
    const boundsShifted = !prevOv ||
      Math.abs(visible.x1 - prevOv.x1) > 5 || Math.abs(visible.y1 - prevOv.y1) > 5 ||
      Math.abs(visible.x2 - prevOv.x2) > 5 || Math.abs(visible.y2 - prevOv.y2) > 5;
    const throttleElapsed = !prevOv || (now - prevOv.time) >= OVERLAY_THROTTLE_MS;

    if (boundsShifted || throttleElapsed) {
      overlayBoundsRef.current = { ...visible, time: now };
      if (zoom >= RIVER_ZOOM_THRESHOLD) {
        void fetchRiversAndLakes(clampBounds(visible));
      }
      void fetchClaims(visible);
      void fetchClaimTiles(visible);
    }

    if (cells.length <= PREFETCH_CELL_LIMIT) {
      const visibleSet = new Set(cells.map((c) => `${c.cellX},${c.cellY}`));
      const border: { cellX: number; cellY: number }[] = [];
      for (const c of cells) {
        for (const [dx, dy] of [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[1,-1],[-1,1],[1,1]] as const) {
          const nx = c.cellX + dx;
          const ny = c.cellY + dy;
          const k = `${nx},${ny}`;
          if (!visibleSet.has(k) && !regionsRef.current.has(k) && !inFlightRef.current.has(k)) {
            if (worldW > 0 && (nx * GRID >= worldW || nx < 0)) continue;
            if (worldH > 0 && (ny * GRID >= worldH || ny < 0)) continue;
            visibleSet.add(k);
            border.push({ cellX: nx, cellY: ny });
          }
        }
      }
      if (border.length > 0) {
        const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : (fn: () => void) => setTimeout(fn, 0);
        idle(() => {
          let pIdx = 0;
          let pFlight = 0;
          const pDispatch = () => {
            while (pFlight < 4 && pIdx < border.length) {
              const c = border[pIdx++]!;
              pFlight++;
              fetchCell(c.cellX, c.cellY, mw, genId).finally(() => {
                pFlight--;
                pDispatch();
              });
            }
          };
          pDispatch();
        });
      }
    }

    scheduleFrame();
  }, [fetchCell, fetchRiversAndLakes, fetchClaims, fetchClaimTiles, scheduleFrame, worldW, worldH, currentWorld, generationId]);

  const scheduleLoad = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void loadRegion(), DEBOUNCE_MS);
  }, [loadRegion]);

  // Pre-fetch rivers for claim area on entering expansion mode (A-9)
  useEffect(() => {
    if (!inExpansion || expandingChunks.size === 0) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const ck of expandingChunks) {
      const [x, y] = ck.split(',').map(Number) as [number, number];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const margin = 5;
    const bounds = clampBounds({
      x1: minX - margin,
      y1: minY - margin,
      x2: maxX + margin,
      y2: maxY + margin,
    });
    void fetchRiversAndLakes(bounds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inExpansion]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'g' || e.key === 'G') {
        setShowGrid((prev) => !prev);
        return;
      }

      if (!inExpansion) return;

      if (e.key === 'b' || e.key === 'B') { setActiveTool('brush'); }
      else if (e.key === 'f' || e.key === 'F') { setActiveTool('fill'); }
      else if (e.key === 's' || e.key === 'S') { setActiveTool('snap'); }
      else if (e.key === 'e' || e.key === 'E') { setActiveTool('eraser'); }
      else if (e.key === '[') {
        const idx = BRUSH_STEPS.indexOf(brushSize);
        if (idx > 0) {
          setBrushSize(BRUSH_STEPS[idx - 1]!);
        } else if (idx === -1) {
          for (let i = BRUSH_STEPS.length - 1; i >= 0; i--) {
            if (BRUSH_STEPS[i]! < brushSize) { setBrushSize(BRUSH_STEPS[i]!); break; }
          }
        }
      }
      else if (e.key === ']') {
        const idx = BRUSH_STEPS.indexOf(brushSize);
        if (idx >= 0 && idx < BRUSH_STEPS.length - 1) {
          setBrushSize(BRUSH_STEPS[idx + 1]!);
        } else if (idx === -1) {
          const next = BRUSH_STEPS.find((s: number) => s > brushSize);
          if (next) setBrushSize(next);
        }
      }
      else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); undo(); }
      else if (e.key === 'Escape') {
        if (pendingAdditions.size > 0 || pendingRemovals.size > 0) {
          if (confirm('Discard pending changes?')) exitExpansion();
        } else {
          exitExpansion();
        }
      }
      else if (e.key === 'Enter') {
        if (pendingAdditions.size > 0 || pendingRemovals.size > 0) {
          void commitExpansion();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [
    inExpansion, brushSize, pendingAdditions, pendingRemovals,
    setActiveTool, setBrushSize, undo, exitExpansion, commitExpansion,
  ]);

  // Create renderer on mount, replace resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const renderer = createMapRenderer(container);
    rendererRef.current = renderer;
    const ro = new ResizeObserver(() => {
      renderer.resize(container.clientWidth, container.clientHeight);
      scheduleFrame();
    });
    ro.observe(container);
    renderer.resize(container.clientWidth, container.clientHeight);
    return () => {
      ro.disconnect();
      renderer.destroy();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial centering and first load
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    const v = viewRef.current;

    // Center on most recent claim, or world midpoint
    const sorted = [...myClaims].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    const target = sorted[0];

    let centerX: number;
    let centerY: number;

    if (target) {
      centerX = target.origin.x + 64;
      centerY = target.origin.y + 64;
    } else if (worldW > 0 && worldH > 0) {
      centerX = worldW / 2;
      centerY = worldH / 2;
    } else {
      centerX = INITIAL_CHUNKS / 2;
      centerY = INITIAL_CHUNKS / 2;
    }

    const cellPx = CELL_SIZE * v.zoom;
    v.offsetX = w / 2 - centerX * cellPx;
    v.offsetY = h / 2 - centerY * cellPx;
    clampView(v, container);
    void loadRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const v = viewRef.current;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, v.zoom * factor));
      const ratio = newZoom / v.zoom;
      v.offsetX = mx - (mx - v.offsetX) * ratio;
      v.offsetY = my - (my - v.offsetY) * ratio;
      v.zoom = newZoom;
      clampView(v, container);
      const renderer = rendererRef.current;
      if (renderer) {
        renderer.invalidate('terrain');
        renderer.invalidate('hydro');
        renderer.invalidate('claims');
        renderer.invalidate('ui');
      }
      scheduleFrame();
      scheduleLoad();
    };
    container.addEventListener('wheel', handler, { passive: false });
    return () => container.removeEventListener('wheel', handler);
  }, [scheduleFrame, scheduleLoad, clampView]);

  // Scoped invalidation: UI layer (hover, selection, grid)
  useEffect(() => {
    rendererRef.current?.invalidate('ui');
    scheduleFrame();
  }, [hoverChunk, selectedChunk, showGrid, scheduleFrame]);

  // Scoped invalidation: claims layer
  useEffect(() => {
    rendererRef.current?.invalidate('claims');
    scheduleFrame();
  }, [visibleClaims, claimTiles, pendingAdditions, pendingRemovals, validationErrors, islandChunks, scheduleFrame]);

  const applyExpansionTool = useCallback(
    (chunk: ChunkCoordJson) => {
      const claimed = allClaimedSet();

      if (activeTool === 'brush') {
        const result = brushPaint(chunk.x, chunk.y, brushSize, expandingChunks, pendingAdditions, claimed);
        if (result.length > 0) paintChunks(result);
      } else if (activeTool === 'fill') {
        const tile = lookupTile(regionsRef.current, chunk.x, chunk.y);
        if (tile) {
          const result = fillByBiome(
            chunk.x, chunk.y, tile.biome,
            expandingChunks, pendingAdditions, claimed, tileLookup,
          );
          if (result.length > 0) paintChunks(result);
        }
      } else if (activeTool === 'snap') {
        const result = snapToRiver(
          chunk.x, chunk.y, riversRef.current,
          expandingChunks, pendingAdditions, claimed,
        );
        if (result.length > 0) paintChunks(result);
      } else if (activeTool === 'eraser') {
        const ck = chunkKey(chunk);
        const cs = candidateSet(expandingChunks, pendingAdditions, pendingRemovals);
        if (!cs.has(ck)) return;
        if (!isBorderChunk(chunk.x, chunk.y, cs)) return;

        // Check that erasing won't disconnect
        const test = new Set(cs);
        test.delete(ck);
        if (expandingClaim) {
          const disc = checkContiguityLocal(expandingClaim.origin, test);
          if (disc.length > 0) return;
        }
        eraseChunks([chunk]);
      }
    },
    [
      activeTool, brushSize, expandingChunks, pendingAdditions, pendingRemovals,
      expandingClaim, paintChunks, eraseChunks, allClaimedSet,
    ],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (showPanel) return;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startOffX: viewRef.current.offsetX,
        startOffY: viewRef.current.offsetY,
      };

      if (inExpansion && (activeTool === 'brush' || activeTool === 'eraser')) {
        paintingRef.current = true;
        strokeChunksRef.current = [];
        const chunk = screenToChunk(e.clientX, e.clientY);
        if (chunk) {
          applyExpansionTool(chunk);
          strokeChunksRef.current.push(chunk);
        }
      }
    },
    [showPanel, inExpansion, activeTool, screenToChunk, applyExpansionTool],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;

        if (paintingRef.current && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
          paintingRef.current = false;
        }

        if (!paintingRef.current) {
          viewRef.current.offsetX = dragRef.current.startOffX + dx;
          viewRef.current.offsetY = dragRef.current.startOffY + dy;
          if (containerRef.current) clampView(viewRef.current, containerRef.current);
          scheduleFrame();
          return;
        }

        // Painting while dragging
        const chunk = screenToChunk(e.clientX, e.clientY);
        if (chunk) {
          const last = strokeChunksRef.current[strokeChunksRef.current.length - 1];
          if (!last || last.x !== chunk.x || last.y !== chunk.y) {
            applyExpansionTool(chunk);
            strokeChunksRef.current.push(chunk);
          }
        }
        return;
      }
      const chunk = screenToChunk(e.clientX, e.clientY);
      setHoverChunk((prev) => {
        if (prev && chunk && prev.x === chunk.x && prev.y === chunk.y) return prev;
        if (!prev && !chunk) return prev;
        return chunk;
      });

      if (chunk && !showPanel) {
        const hit = findClaimAtChunk(chunk);
        if (hit) {
          const prev = hoverClaimRef.current;
          if (prev && prev.claimId === hit.claimId) {
            prev.screenX = e.clientX;
            prev.screenY = e.clientY;
            setHoverClaim({ ...prev });
            return;
          }
          if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
          hoverClaimRef.current = null;
          setHoverClaim(null);
          hoverTimerRef.current = setTimeout(() => {
            const state = {
              claimId: hit.claimId,
              screenX: e.clientX,
              screenY: e.clientY,
            };
            hoverClaimRef.current = state;
            setHoverClaim(state);
          }, 150);
          return;
        }
      }
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverClaimRef.current = null;
      setHoverClaim(null);
    },
    [scheduleFrame, screenToChunk, applyExpansionTool, findClaimAtChunk, showPanel, clampView],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      const wasPainting = paintingRef.current;
      paintingRef.current = false;
      strokeChunksRef.current = [];

      const wasDrag =
        dragRef.current &&
        (Math.abs(e.clientX - dragRef.current.startX) > 3 ||
          Math.abs(e.clientY - dragRef.current.startY) > 3);
      dragRef.current = null;

      if (wasDrag && !wasPainting) {
        scheduleLoad();
        return;
      }

      if (wasPainting) return;

      const chunk = screenToChunk(e.clientX, e.clientY);
      if (!chunk) return;

      if (inExpansion) {
        applyExpansionTool(chunk);
        return;
      }

      // Check if clicking own claim tile
      const ck = chunkKey(chunk);
      const ownTileOverlay = claimTilesRef.current.find(
        (ct) => ct.is_own && ct.tiles.some((t) => chunkKey(t) === ck),
      );
      if (ownTileOverlay) {
        setSelectedOwnClaim(ownTileOverlay.claim_id);
        return;
      }

      // Click on unclaimed space — open placement panel directly
      setSelectedOwnClaim(null);
      const bx = Math.floor(chunk.x / BLOCK_SIZE) * BLOCK_SIZE;
      const by = Math.floor(chunk.y / BLOCK_SIZE) * BLOCK_SIZE;
      if (isBlockAreaClaimed(bx, by)) return;
      selectChunk(chunk);
      setShowPanel(true);
    },
    [
      inExpansion, myClaims,
      scheduleLoad, screenToChunk,
      isBlockAreaClaimed, selectChunk, applyExpansionTool,
    ],
  );

  // Smart quick-embark
  const handleQuickEmbark = useCallback(() => {
    let bestScore = -Infinity;
    let bestChunk: ChunkCoordJson | null = null;

    const excluded = new Set<BiomeKind>(['Mountain', 'Desert', 'Ocean', 'Coast']);

    const claimedTiles = allClaimedSet();

    const riverPoints = new Set<string>();
    for (const river of riversRef.current) {
      for (const pt of river.points) {
        riverPoints.add(`${pt.x},${pt.y}`);
      }
    }

    const claimOrigins = myClaims.map((c) => c.origin);
    function nearestClaimDist(cx: number, cy: number): number {
      let min = Infinity;
      for (const o of claimOrigins) {
        const d = Math.abs(o.x - cx) + Math.abs(o.y - cy);
        if (d < min) min = d;
      }
      return min;
    }

    function hasRiverNearby(cx: number, cy: number): boolean {
      for (let dx = -2; dx <= 2; dx++) {
        for (let dy = -2; dy <= 2; dy++) {
          if (riverPoints.has(`${cx + dx},${cy + dy}`)) return true;
        }
      }
      return false;
    }

    for (const region of regionsRef.current.values()) {
      for (let ry = 0; ry < region.tiles.length; ry++) {
        const row = region.tiles[ry]!;
        for (let rx = 0; rx < row.length; rx++) {
          const tile = row[rx]!;
          if (excluded.has(tile.biome)) continue;
          const cx = region.x1 + rx;
          const cy = region.y1 + ry;
          if (claimedTiles.has(chunkKey({ x: cx, y: cy }))) continue;

          let score = 0;
          if (tile.biome === 'Grassland') score += 3.0;
          else if (tile.biome === 'Forest') score += 2.0;

          if (hasRiverNearby(cx, cy)) score += 2.0;

          const claimDist = nearestClaimDist(cx, cy);
          score += Math.min(5.0, claimDist);

          if (score > bestScore || (score === bestScore && bestChunk && cy < bestChunk.y)) {
            bestScore = score;
            bestChunk = { x: cx, y: cy };
          }
        }
      }
    }

    if (!bestChunk) {
      useToasts.getState().push('info', 'No suitable chunks found in cached region');
      return;
    }

    const container = containerRef.current;
    if (!container) return;
    const cellPx = CELL_SIZE * viewRef.current.zoom;
    viewRef.current.offsetX = container.clientWidth / 2 - bestChunk.x * cellPx - cellPx / 2;
    viewRef.current.offsetY = container.clientHeight / 2 - bestChunk.y * cellPx - cellPx / 2;
    clampView(viewRef.current, container);
    setHoverChunk(bestChunk);
    scheduleFrame();
    scheduleLoad();
  }, [myClaims, allClaimedSet, scheduleFrame, scheduleLoad, clampView]);

  const handlePanelClose = useCallback(() => {
    setShowPanel(false);
    selectChunk(null);
  }, [selectChunk]);

  const handleClaimCreated = useCallback(() => {
    setShowPanel(false);
    void loadRegion();
  }, [loadRegion]);

  const handleExpandClick = useCallback(() => {
    if (selectedOwnClaim != null) {
      void enterExpansion(selectedOwnClaim);
      setSelectedOwnClaim(null);
    }
  }, [selectedOwnClaim, enterExpansion]);

  const handleCancelExpansion = useCallback(() => {
    if (pendingAdditions.size > 0 || pendingRemovals.size > 0) {
      if (!confirm('Discard pending changes?')) return;
    }
    exitExpansion();
  }, [pendingAdditions, pendingRemovals, exitExpansion]);

  const handleApply = useCallback(() => {
    void commitExpansion();
  }, [commitExpansion]);

  // Info panel data
  const hoverTile = hoverChunk
    ? lookupTile(regionsRef.current, hoverChunk.x, hoverChunk.y)
    : null;

  const activeChunk = hoverChunk ?? selectedChunk;
  const activeTile = activeChunk
    ? lookupTile(regionsRef.current, activeChunk.x, activeChunk.y)
    : null;

  const coordLine = activeChunk
    ? `Chunk (${activeChunk.x}, ${activeChunk.y})`
    : '';
  const tileLine = activeChunk
    ? `Tile (${activeChunk.x * CHUNK_TILES}, ${activeChunk.y * CHUNK_TILES})`
    : '';

  // Cost preview
  const costPreview = inExpansion && expandingClaim
    ? computeCostPreview(
        expandingClaim.block_count,
        pendingAdditions.size,
        pendingRemovals.size,
      )
    : null;

  const hasPending = pendingAdditions.size > 0 || pendingRemovals.size > 0;

  return (
    <div className={styles.mapContainer}>
      <div
        ref={containerRef}
        className={styles.mapCanvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      />

      {/* Toolbar */}
      {inExpansion ? (
        <div className={styles.mapToolbar}>
          <div className={styles.paintToolbar}>
            {(Object.keys(TOOL_LABELS) as PaintTool[]).map((tool) => (
              <button
                key={tool}
                className={`${styles.toolBtn} ${activeTool === tool ? styles.toolBtnActive : ''}`}
                onClick={() => setActiveTool(tool)}
                title={TOOL_LABELS[tool]}
              >
                {TOOL_LABELS[tool]}
              </button>
            ))}

            <span className={styles.toolSep}>|</span>

            <span className={styles.brushSizeLabel}>Size</span>
            <input
              type="range"
              min={0}
              max={BRUSH_STEPS.length - 1}
              value={BRUSH_STEPS.indexOf(brushSize) >= 0
                ? BRUSH_STEPS.indexOf(brushSize)
                : BRUSH_STEPS.findIndex((s) => s >= brushSize)}
              onChange={(e) => setBrushSize(BRUSH_STEPS[Number(e.target.value)]!)}
              className={styles.brushSlider}
              title={`Brush size: ${brushSize}×${brushSize} ([ / ] to adjust)`}
            />
            <span className={styles.brushSizeValue}>{brushSize}×{brushSize}</span>

            <span className={styles.toolSep}>|</span>

            <button
              className={styles.toolBtn}
              onClick={undo}
              disabled={undoStack.length === 0}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>

            <span className={styles.toolSep}>|</span>

            {costPreview && costPreview.deltaTiles !== 0 && (
              <span className={styles.costPreview}>
                {costPreview.deltaTiles > 0 ? '+' : ''}{costPreview.deltaTiles.toLocaleString()} tiles
                {costPreview.deltaParcels !== 0 && (
                  <> ({costPreview.deltaParcels > 0 ? '+' : ''}{costPreview.deltaParcels} parcel{Math.abs(costPreview.deltaParcels) !== 1 ? 's' : ''}{' '}
                  {costPreview.deltaCost >= 0
                    ? `$${costPreview.deltaCost.toFixed(2)}/mo`
                    : costPreview.breakdown}
                  {costPreview.breakdown && costPreview.deltaCost >= 0 && (
                    <span className={styles.costBreakdown}> {costPreview.breakdown}</span>
                  )})</>
                )}
              </span>
            )}

            <span className={styles.toolSep}>|</span>

            <button
              className={styles.applyBtn}
              onClick={handleApply}
              disabled={!hasPending || commitInProgress}
            >
              {commitInProgress ? 'Applying...' : 'Apply'}
            </button>
            <button
              className={styles.cancelBtn}
              onClick={handleCancelExpansion}
              disabled={commitInProgress}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.mapToolbar}>
          {selectedOwnClaim != null && (
            <button
              className={styles.primaryBtn}
              onClick={handleExpandClick}
            >
              Expand
            </button>
          )}
          <button
            className={styles.secondaryBtn}
            onClick={handleQuickEmbark}
            title="Find me a good spot"
          >
            Quick Embark
          </button>
          <button
            className={`${styles.secondaryBtn} ${styles.gridBtn}`}
            onClick={() => setShowGrid((prev) => !prev)}
            title="Toggle grid (G)"
          >
            Grid: {showGrid ? 'On' : 'Off'}
          </button>
        </div>
      )}

      <div className={styles.mapCoords}>
        {activeChunk ? (
          <>
            <span className={styles.coordChunk}>{coordLine}</span>
            {' — '}
            <span className={styles.coordTile}>{tileLine}</span>
            {activeTile && (
              <>
                <br />
                <span>
                  {activeTile.biome} &middot; {fmtElevation(activeTile.elevation)} &middot;{' '}
                  {fmtTemperature(activeTile.temperature)} &middot;{' '}
                  {fmtMoisture(activeTile.moisture)} moisture
                </span>
                <br />
                <span className={styles.coordDetail}>
                  {activeTile.soil_type} &middot;{' '}
                  {activeTile.rock_type ?? '—'} &middot;{' '}
                  {activeTile.mineral_density.toFixed(2)} mineral
                </span>
              </>
            )}
            {!activeTile && ' — Click to place'}
          </>
        ) : (
          <span>
            {inExpansion
              ? 'Paint chunks to expand your claim'
              : 'Click to place or select'}
          </span>
        )}
      </div>
      {hoverClaim && !showPanel && !inExpansion && (() => {
        const overlay = claimsRef.current.find(
          (c) => c.claim_id === hoverClaim.claimId,
        );
        if (!overlay) return null;
        const ownMeta = myClaims.find(
          (c) => c.claim_id === hoverClaim.claimId,
        );
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return null;

        const tooltipData: ClaimTooltipData = {
          name: overlay.name,
          ownerName: overlay.owner_name,
          status: overlay.status as ClaimTooltipData['status'],
          tileCount: overlay.block_count,
          biome: computeClaimBiome(hoverClaim.claimId),
          isOwn: !!ownMeta,
          visibility: ownMeta
            ? (ownMeta.visibility as ClaimTooltipData['visibility'])
            : null,
        };

        const tx = hoverClaim.screenX - rect.left + 12;
        const ty = hoverClaim.screenY - rect.top + 12;

        return (
          <ClaimTooltip
            data={tooltipData}
            x={tx}
            y={ty}
          />
        );
      })()}
      {showPanel && selectedChunk && (
        <ClaimPanel
          chunk={selectedChunk}
          biome={hoverTile?.biome ?? null}
          onCancel={handlePanelClose}
          onCreated={handleClaimCreated}
        />
      )}
    </div>
  );
}
